import L from 'leaflet';

export type LabelSlot = 'right' | 'topRight' | 'bottomRight' | 'left' | 'topLeft' | 'bottomLeft';

export interface LabelBox {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

export interface LabelObstacle extends LabelBox {
    element?: Element;
}

export interface LabelCandidate {
    marker: L.Marker;
    icon: Element;
    x: number;
    y: number;
    gap: number;
    width: number;
    height: number;
    priority: number;
    previousSlot?: LabelSlot;
}

export interface MapLabelLayoutOptions {
    keepPreviousSlot?: boolean;
    isPreferred?: (marker: L.Marker) => boolean;
    requestFrame?: (callback: () => void) => number;
}

/** Tried in key order; vertical slots sit above (-1) or below (1) the marker with the arrow over it */
const LABEL_SLOTS: Record<LabelSlot, { direction: 'right' | 'left'; vertical: number }> = {
    right: { direction: 'right', vertical: 0 },
    topRight: { direction: 'right', vertical: -1 },
    bottomRight: { direction: 'right', vertical: 1 },
    left: { direction: 'left', vertical: 0 },
    topLeft: { direction: 'left', vertical: -1 },
    bottomLeft: { direction: 'left', vertical: 1 },
};

/** How far the arrow protrudes, and so the gap between a label and its marker */
const LABEL_ARROW_SIZE = 8;
/** Distance from a diagonal label's near corner to its arrow */
const LABEL_ARROW_INSET = 18;

const SLOT_ORDER = Object.keys(LABEL_SLOTS) as LabelSlot[];
const CELL_SIZE = 64;
const LABEL_CLASS = 'map-label';
const HIDDEN_CLASS = 'map-label-hidden';

class BoxGrid {
    private cells = new Map<string, LabelObstacle[]>();

    addBox(box: LabelObstacle) {
        for (const key of this.getCellKeys(box)) {
            const cell = this.cells.get(key) ?? [];
            cell.push(box);
            this.cells.set(key, cell);
        }
    }

    hasCollision(box: LabelBox, ignoreElement: Element) {
        return this.getCellKeys(box).some(key =>
            (this.cells.get(key) ?? []).some(other => other.element !== ignoreElement && isOverlapping(box, other))
        );
    }

    private getCellKeys(box: LabelBox) {
        const keys: string[] = [];
        for (let col = Math.floor(box.left / CELL_SIZE); col <= Math.floor(box.right / CELL_SIZE); col++) {
            for (let row = Math.floor(box.top / CELL_SIZE); row <= Math.floor(box.bottom / CELL_SIZE); row++) {
                keys.push(`${col}:${row}`);
            }
        }
        return keys;
    }
}

function isOverlapping(a: LabelBox, b: LabelBox) {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/** Custom Leaflet plugin that calculates tooltip label layouts to prevent them from overlapping  */
export class MapLabelLayout {

    private enabled = true;
    private slots = new WeakMap<L.Marker, LabelSlot | null>();
    private hoverMarkers = new WeakSet<L.Marker>();
    private frame: number = null;
    private requestFrame: (callback: () => void) => number;

    static getLabelBox(candidate: LabelCandidate, slot: LabelSlot): LabelBox {
        const { direction, vertical } = LABEL_SLOTS[slot];
        let left = candidate.x + candidate.gap + LABEL_ARROW_SIZE;
        let top = candidate.y - candidate.height / 2;

        if (vertical === 0 && direction === 'left') {
            left = candidate.x - candidate.gap - LABEL_ARROW_SIZE - candidate.width;
        } else if (vertical !== 0 && direction === 'right') {
            left = candidate.x - LABEL_ARROW_INSET;
        } else if (vertical !== 0) {
            left = candidate.x + LABEL_ARROW_INSET - candidate.width;
        }

        if (vertical < 0) {
            top = candidate.y - candidate.gap - LABEL_ARROW_SIZE - candidate.height;
        } else if (vertical > 0) {
            top = candidate.y + candidate.gap + LABEL_ARROW_SIZE;
        }

        return { left, top, right: left + candidate.width, bottom: top + candidate.height };
    }

    /** The offset from where Leaflet puts a tooltip in the slot's direction to the slot's box */
    static getLabelOffset(candidate: LabelCandidate, slot: LabelSlot): [number, number] {
        const box = MapLabelLayout.getLabelBox(candidate, slot);
        // Leaflet's 'left' direction doesn't mirror the tooltip anchor, so it still adds the gap
        let leafletLeft = candidate.x + candidate.gap + LABEL_ARROW_SIZE;
        if (LABEL_SLOTS[slot].direction === 'left') {
            leafletLeft = candidate.x + candidate.gap - LABEL_ARROW_SIZE - candidate.width;
        }
        return [box.left - leafletLeft, box.top - (candidate.y - candidate.height / 2)];
    }

    /** A label that fits in no slot is mapped to null */
    static findLabelPlacements(candidates: LabelCandidate[], obstacles: LabelObstacle[], keepPreviousSlot = false) {
        const grid = new BoxGrid();
        obstacles.forEach(obstacle => grid.addBox(obstacle));

        const placements = new Map<LabelCandidate, LabelSlot | null>();

        for (const candidate of [...candidates].sort((a, b) => b.priority - a.priority)) {
            let slots = SLOT_ORDER;
            if (keepPreviousSlot && candidate.previousSlot) {
                slots = [candidate.previousSlot, ...SLOT_ORDER.filter(slot => slot !== candidate.previousSlot)];
            }

            const placed = slots.find(slot => !grid.hasCollision(MapLabelLayout.getLabelBox(candidate, slot), candidate.icon)) ?? null;
            if (placed) {
                grid.addBox(MapLabelLayout.getLabelBox(candidate, placed));
            }
            placements.set(candidate, placed);
        }

        return placements;
    }

    constructor(private map: L.Map, private options: MapLabelLayoutOptions = {}) {
        this.requestFrame = options.requestFrame ?? (callback => requestAnimationFrame(callback));
        map.getContainer().style.setProperty('--map-label-arrow-size', `${LABEL_ARROW_SIZE}px`);
        map.getContainer().style.setProperty('--map-label-arrow-inset', `${LABEL_ARROW_INSET}px`);
        map.on('zoomend moveend layerremove', this.update, this);
        map.on('layeradd', this.handleLayerAdd, this);
    }

    enable() {
        this.enabled = true;
        this.rebindLabels();
        this.update();
    }

    disable() {
        this.enabled = false;
        this.slots = new WeakMap();
        this.rebindLabels();
    }

    getTooltipOptions(): L.TooltipOptions {
        return {
            permanent: this.enabled,
            pane: this.enabled ? 'markerPane' : 'tooltipPane',
            className: LABEL_CLASS,
            direction: 'right',
            offset: [0, 0],
        };
    }

    update() {
        if (this.frame === null) {
            this.frame = this.requestFrame(() => {
                this.frame = null;
                this.arrangeLabels();
            });
        }
    }

    destroy() {
        cancelAnimationFrame(this.frame);
        this.map.off('zoomend moveend layerremove', this.update, this);
        this.map.off('layeradd', this.handleLayerAdd, this);
    }

    private getMarkers() {
        const markers: L.Marker[] = [];
        this.map.eachLayer(layer => {
            if (layer instanceof L.Marker) {
                markers.push(layer);
            }
        });
        return markers;
    }

    private getLabel(marker: L.Marker): L.Tooltip | undefined {
        const tooltip = marker.getTooltip();
        return tooltip?.options.className === LABEL_CLASS ? tooltip : undefined;
    }

    private rebindLabel(marker: L.Marker, tooltip: L.Tooltip) {
        tooltip.getElement()?.classList.remove(HIDDEN_CLASS);
        tooltip.getElement()?.removeAttribute('data-label-slot');
        marker.unbindTooltip().bindTooltip(tooltip, this.getTooltipOptions());
    }

    private rebindLabels() {
        for (const marker of this.getMarkers()) {
            const tooltip = this.getLabel(marker);
            if (tooltip) {
                this.rebindLabel(marker, tooltip);
            }
        }
    }

    private handleLayerAdd({ layer }: L.LayerEvent) {
        if (layer instanceof L.Marker) {
            const tooltip = this.getLabel(layer);
            if (!this.hoverMarkers.has(layer)) {
                this.hoverMarkers.add(layer);
                layer.on('mouseover', () => this.revealLabel(layer));
                layer.on('mouseout', () => this.restoreLabel(layer));
            }
            // markers clustered away while labels were toggled missed the rebind
            if (tooltip && tooltip.options.permanent !== this.enabled) {
                this.rebindLabel(layer, tooltip);
            }
        }
        this.update();
    }

    private revealLabel(marker: L.Marker) {
        const element = this.getLabel(marker)?.getElement();
        const pane = this.map.getPane('tooltipPane');
        element?.classList.remove(HIDDEN_CLASS);
        // re-appending the hovered element makes Chrome fire mouseover again, looping and swallowing clicks
        if (element?.isConnected && element.parentNode !== pane) {
            pane.appendChild(element);
        }
    }

    private restoreLabel(marker: L.Marker) {
        const element = this.getLabel(marker)?.getElement();
        const pane = this.map.getPane('markerPane');
        element?.classList.toggle(HIDDEN_CLASS, this.slots.get(marker) === null);
        if (element?.isConnected && this.enabled && element.parentNode !== pane) {
            pane.appendChild(element);
        }
    }

    private arrangeLabels() {
        if (this.enabled) {
            const container = this.map.getContainer();
            const mapRect = container.getBoundingClientRect();
            const candidates: LabelCandidate[] = [];

            for (const marker of this.getMarkers()) {
                const tooltip = this.getLabel(marker);
                const icon = marker.getElement();
                if (icon && tooltip?.isOpen()) {
                    const point = this.map.latLngToContainerPoint(marker.getLatLng());
                    const anchor = L.point(marker.options.icon.options.tooltipAnchor);
                    let priority = 0;
                    if (this.slots.get(marker)) {
                        priority = 1;
                    }
                    if (this.options.isPreferred?.(marker)) {
                        priority = 2;
                    }
                    candidates.push({
                        marker,
                        icon,
                        x: point.x,
                        y: point.y + anchor.y,
                        gap: anchor.x,
                        width: tooltip.getElement().offsetWidth,
                        height: tooltip.getElement().offsetHeight,
                        priority,
                        previousSlot: this.slots.get(marker) ?? undefined,
                    });
                }
            }

            const obstacles = Array.from(container.querySelectorAll('.leaflet-marker-icon')).map(element => {
                const rect = element.getBoundingClientRect();
                return {
                    element,
                    left: rect.left - mapRect.left,
                    top: rect.top - mapRect.top,
                    right: rect.right - mapRect.left,
                    bottom: rect.bottom - mapRect.top,
                };
            });

            MapLabelLayout.findLabelPlacements(candidates, obstacles, this.options.keepPreviousSlot).forEach((slot, candidate) => {
                const tooltip = candidate.marker.getTooltip();
                if (slot) {
                    const offset = L.point(MapLabelLayout.getLabelOffset(candidate, slot));
                    if (tooltip.options.direction !== LABEL_SLOTS[slot].direction || !offset.equals(L.point(tooltip.options.offset))) {
                        tooltip.options.direction = LABEL_SLOTS[slot].direction;
                        tooltip.options.offset = offset;
                        // Leaflet has no public way to reposition a tooltip without re-rendering its content
                        (tooltip as L.Tooltip & { _updatePosition(): void })._updatePosition();
                    }
                    tooltip.getElement().dataset.labelSlot = slot;
                }
                tooltip.getElement().classList.toggle(HIDDEN_CLASS, !slot && !candidate.icon.matches(':hover'));
                this.slots.set(candidate.marker, slot);
            });
        }
    }
}
